import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users2, FolderKanban, Gavel } from "lucide-react";
import { http } from "@/lib/api";
import { formatDate } from "@/lib/format";

// Busca central — um campo só que procura em clientes, processos e audiências/prazos
// ao mesmo tempo (pedido do Robson: evitar ter que abrir aba por aba procurando algo).
// Debounce de 300ms pra não bater no /search a cada tecla.
const EMPTY = { clients: [], processos: [], hearings: [] };

export default function GlobalSearch({ variant = "sidebar", onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(EMPTY);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await http.get("/search", { params: { q: term } });
        setResults(r.data);
      } catch {
        /* handled by interceptor */
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const clear = () => {
    setQ("");
    setResults(EMPTY);
  };

  const go = (path) => {
    navigate(path);
    setOpen(false);
    clear();
    onNavigate?.();
  };

  const hasQuery = q.trim().length >= 2;
  const hasResults = results.clients.length || results.processos.length || results.hearings.length;
  const dark = variant === "sidebar";

  return (
    <div ref={boxRef} className="relative">
      <div
        className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
          dark
            ? "bg-stone-950 border-stone-800 focus-within:border-stone-600"
            : "bg-stone-100 border-stone-200 focus-within:border-stone-400"
        }`}
      >
        <Search className={`w-4 h-4 shrink-0 ${dark ? "text-stone-500" : "text-stone-400"}`} strokeWidth={1.75} />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Buscar cliente, processo, audiência…"
          className={`flex-1 min-w-0 bg-transparent text-sm outline-none ${
            dark ? "text-stone-100 placeholder:text-stone-500" : "text-stone-900 placeholder:text-stone-400"
          }`}
        />
        {q && (
          <button type="button" onClick={clear} aria-label="Limpar busca">
            <X className={`w-3.5 h-3.5 ${dark ? "text-stone-500" : "text-stone-400"}`} />
          </button>
        )}
      </div>

      {open && hasQuery && (
        <div
          data-testid="global-search-results"
          className={`absolute z-50 mt-2 w-[22rem] max-w-[90vw] rounded-md shadow-lg max-h-[26rem] overflow-y-auto border ${
            dark ? "bg-stone-900 border-stone-800" : "bg-white border-stone-200"
          }`}
        >
          {loading && <div className="px-4 py-3 text-xs text-stone-500">Buscando…</div>}

          {!loading && !hasResults && (
            <div className="px-4 py-3 text-xs text-stone-500">Nada encontrado para "{q.trim()}"</div>
          )}

          {!loading && results.clients.length > 0 && (
            <ResultGroup label="Clientes" icon={Users2}>
              {results.clients.map((c) => (
                <ResultRow key={c.client_id} title={c.name} subtitle={c.phone} onClick={() => go("/clientes")} dark={dark} />
              ))}
            </ResultGroup>
          )}

          {!loading && results.processos.length > 0 && (
            <ResultGroup label="Processos" icon={FolderKanban}>
              {results.processos.map((p) => (
                <ResultRow
                  key={p.processo_id}
                  title={p.apelido || p.numero}
                  subtitle={p.apelido ? p.numero : undefined}
                  onClick={() => go("/processos")}
                  dark={dark}
                />
              ))}
            </ResultGroup>
          )}

          {!loading && results.hearings.length > 0 && (
            <ResultGroup label="Audiências e prazos" icon={Gavel}>
              {results.hearings.map((h) => (
                <ResultRow
                  key={h.pub_id}
                  title={h.parties || h.case_number || "Compromisso"}
                  subtitle={[formatDate(h.date), h.case_number].filter(Boolean).join(" · ")}
                  onClick={() => go(`/audiencia/${h.pub_id}`)}
                  dark={dark}
                />
              ))}
            </ResultGroup>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, icon: Icon, children }) {
  return (
    <div className="py-2">
      <div className="px-4 pb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-500">
        <Icon className="w-3 h-3" strokeWidth={2} />
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ title, subtitle, onClick, dark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-2 transition-colors ${dark ? "hover:bg-stone-800" : "hover:bg-stone-100"}`}
    >
      <div className={`text-sm truncate ${dark ? "text-stone-200" : "text-stone-900"}`}>{title || "—"}</div>
      {subtitle && <div className="text-xs text-stone-500 truncate">{subtitle}</div>}
    </button>
  );
}
